import { useMemo } from 'react';
import type { AdminPost } from '@/mocks/adminPosts';

interface InsightsPanelProps {
  posts: AdminPost[];
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

export default function InsightsPanel({ posts }: InsightsPanelProps) {
  const publishedPosts = useMemo(() => posts.filter((p) => p.status === 'publicado'), [posts]);
  const scheduledPosts = useMemo(() => posts.filter((p) => p.status === 'programado'), [posts]);
  const draftPosts = useMemo(() => posts.filter((p) => p.status === 'borrador'), [posts]);

  const totalStats = useMemo(() => {
    const stats = { likes: 0, comments: 0, shares: 0, reach: 0 };
    publishedPosts.forEach((p) => {
      stats.likes += p.engagement.likes;
      stats.comments += p.engagement.comments;
      stats.shares += p.engagement.shares;
      stats.reach += p.engagement.reach;
    });
    return stats;
  }, [publishedPosts]);

  const topPosts = useMemo(() => {
    return [...publishedPosts]
      .sort((a, b) => {
        const aTotal = a.engagement.likes + a.engagement.comments * 2 + a.engagement.shares * 3;
        const bTotal = b.engagement.likes + b.engagement.comments * 2 + b.engagement.shares * 3;
        return bTotal - aTotal;
      })
      .slice(0, 5);
  }, [publishedPosts]);

  return (
    <aside className="w-[300px] shrink-0 space-y-4 hidden xl:block">
      {/* ── Resumen de Engagement ── */}
      <div className="bg-background-50 rounded-xl border border-background-200 p-4">
        <h4 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
          <i className="ri-bar-chart-2-line text-accent-500 text-base"></i>
          Engagement Total
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-xl bg-background-100 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <i className="ri-thumb-up-fill text-primary-500 text-sm"></i>
            </div>
            <p className="text-lg font-bold text-foreground-900">{formatCount(totalStats.likes)}</p>
            <p className="text-[10px] text-foreground-400 font-medium">Me gusta</p>
          </div>
          <div className="p-3 rounded-xl bg-background-100 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <i className="ri-chat-3-fill text-emerald-500 text-sm"></i>
            </div>
            <p className="text-lg font-bold text-foreground-900">{formatCount(totalStats.comments)}</p>
            <p className="text-[10px] text-foreground-400 font-medium">Comentarios</p>
          </div>
          <div className="p-3 rounded-xl bg-background-100 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <i className="ri-share-forward-fill text-amber-500 text-sm"></i>
            </div>
            <p className="text-lg font-bold text-foreground-900">{formatCount(totalStats.shares)}</p>
            <p className="text-[10px] text-foreground-400 font-medium">Compartidos</p>
          </div>
          <div className="p-3 rounded-xl bg-background-100 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <i className="ri-eye-fill text-accent-500 text-sm"></i>
            </div>
            <p className="text-lg font-bold text-foreground-900">{formatCount(totalStats.reach)}</p>
            <p className="text-[10px] text-foreground-400 font-medium">Alcance</p>
          </div>
        </div>
      </div>

      {/* ── Top Publicaciones ── */}
      <div className="bg-background-50 rounded-xl border border-background-200 p-4">
        <h4 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
          <i className="ri-fire-line text-amber-500 text-base"></i>
          Top Publicaciones
        </h4>
        {topPosts.length === 0 ? (
          <p className="text-xs text-foreground-400 text-center py-3">
            No hay publicaciones todavía.
          </p>
        ) : (
          <div className="space-y-3">
            {topPosts.map((p, idx) => {
              const engagement = p.engagement.likes + p.engagement.comments * 2 + p.engagement.shares * 3;
              return (
                <div key={p.id} className="flex items-start gap-3 group">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                    idx === 0 ? 'bg-amber-100 text-amber-700' :
                    idx === 1 ? 'bg-foreground-100 text-foreground-500' :
                    idx === 2 ? 'bg-amber-50 text-amber-600' :
                    'bg-background-100 text-foreground-400'
                  }`}>
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground-800 truncate leading-snug">
                      {p.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-foreground-400">
                        {formatCount(engagement)} interacciones
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Próximas Publicaciones ── */}
      <div className="bg-background-50 rounded-xl border border-background-200 p-4">
        <h4 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
          <i className="ri-calendar-schedule-line text-primary-500 text-base"></i>
          Programadas
        </h4>
        {scheduledPosts.length === 0 ? (
          <p className="text-xs text-foreground-400 text-center py-3">
            No hay publicaciones programadas.
          </p>
        ) : (
          <div className="space-y-3">
            {scheduledPosts
              .sort((a, b) => new Date(a.scheduledDate || '').getTime() - new Date(b.scheduledDate || '').getTime())
              .map((p) => (
                <div key={p.id} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-amber-500 text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground-800 truncate">{p.title}</p>
                    <p className="text-[10px] text-foreground-400 mt-0.5">
                      {p.scheduledDate
                        ? new Date(p.scheduledDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                        : 'Sin fecha'}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* ── Resumen rápido ── */}
      <div className="bg-background-50 rounded-xl border border-background-200 p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-500">Publicadas</span>
          <span className="text-sm font-bold text-emerald-600">{publishedPosts.length}</span>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-foreground-500">Programadas</span>
          <span className="text-sm font-bold text-amber-600">{scheduledPosts.length}</span>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-foreground-500">Borradores</span>
          <span className="text-sm font-bold text-foreground-400">{draftPosts.length}</span>
        </div>
        <div className="border-t border-background-200 mt-2.5 pt-2.5 flex items-center justify-between">
          <span className="text-xs font-semibold text-foreground-700">Total</span>
          <span className="text-sm font-bold text-foreground-900">{posts.length}</span>
        </div>
      </div>
    </aside>
  );
}